import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, CheckCircle, Target, BookOpen, TrendingUp, Award, Zap } from 'lucide-react';

interface CadreReferentielNISTModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CadreReferentielNISTModal({ isOpen, onClose }: CadreReferentielNISTModalProps) {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen) return null;

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: BookOpen },
    { id: 'functions', label: '6 Fonctions', icon: Shield },
    { id: 'implementation', label: 'Implémentation BZ', icon: Target },
    { id: 'excellence', label: 'Excellence BZ', icon: Award },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 rounded-2xl max-w-6xl w-full my-8 border border-indigo-500/30 shadow-2xl shadow-indigo-500/20"
          >
            {/* Header */}
            <div className="relative p-8 border-b border-indigo-500/30">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl">
                    <Shield className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
                      Cadre Référentiel NIST 2.0
                    </h2>
                    <p className="text-gray-400 mt-1 text-lg">
                      Approche holistique de la cybersécurité - Excellence BZ Inc.
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex overflow-x-auto border-b border-indigo-500/30 bg-slate-900/50">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-6 py-4 font-semibold whitespace-nowrap transition-all ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-b-2 border-indigo-400'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Content */}
            <div className="p-8 space-y-8 max-h-[calc(100vh-300px)] overflow-y-auto">
              
              {/* TAB: Vue d'ensemble */}
              {activeTab === 'overview' && (
                <div className="space-y-8">
                  <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-pink-600/20 rounded-xl p-8 border-2 border-indigo-500/50">
                    <h3 className="text-3xl font-bold text-center text-white mb-4">
                      Le Cadre NIST 2.0 : Notre Référence Absolue
                    </h3>
                    <p className="text-gray-300 text-lg text-center leading-relaxed">
                      Chez BZ Inc., nous appliquons le cadre NIST 2.0 de manière <strong className="text-white">holistique</strong> et <strong className="text-white">complète</strong>. 
                      Nous ne faisons pas les choses à moitié. Nous faisons les choses de manière professionnelle, structurée, et excellente.
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl p-6 border border-blue-500/30">
                      <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                        <Target className="w-7 h-7 text-blue-400" />
                        Pourquoi NIST 2.0?
                      </h4>
                      <ul className="space-y-3 text-gray-300">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                          <span><strong className="text-white">Standard international reconnu</strong> - Référence mondiale en cybersécurité</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                          <span><strong className="text-white">Approche holistique</strong> - Couvre tous les aspects de la cybersécurité</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                          <span><strong className="text-white">Évolutif et flexible</strong> - S'adapte à tous les types d'organisations</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                          <span><strong className="text-white">Basé sur les meilleures pratiques</strong> - Résultat de décennies d'expertise</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl p-6 border border-purple-500/30">
                      <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                        <Award className="w-7 h-7 text-purple-400" />
                        L'Excellence BZ Inc.
                      </h4>
                      <p className="text-gray-300 mb-4">
                        Chez BZ Inc., nous ne nous contentons pas d'appliquer le NIST 2.0. Nous l'<strong className="text-white">maîtrisons</strong>, 
                        nous l'<strong className="text-white">adaptons</strong>, et nous l'<strong className="text-white">optimisons</strong> pour nos clients.
                      </p>
                      <ul className="space-y-2 text-gray-300">
                        <li className="flex items-start gap-2">
                          <span className="text-purple-400">✓</span>
                          <span>Implémentation complète et structurée</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-purple-400">✓</span>
                          <span>Documentation professionnelle</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-purple-400">✓</span>
                          <span>Accompagnement continu</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-purple-400">✓</span>
                          <span>Amélioration continue basée sur le cadre</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB: 6 Fonctions */}
              {activeTab === 'functions' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h3 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400 mb-3">
                      Les 6 Fonctions du Cadre NIST 2.0
                    </h3>
                    <p className="text-gray-300 text-lg">
                      Le cadre NIST 2.0 organise la cybersécurité en 6 fonctions essentielles
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {[
                      { id: 1, name: 'GOVERN (Gouvernance)', bgClass: 'from-indigo-500/10 to-indigo-600/10', borderClass: 'border-indigo-500/30', circleBg: 'bg-indigo-500/20', circleBorder: 'border-indigo-500', textClass: 'text-indigo-400', description: 'Établir, communiquer et surveiller la stratégie cybersécurité, les politiques et les procédures organisationnelles.' },
                      { id: 2, name: 'IDENTIFY (Identifier)', bgClass: 'from-blue-500/10 to-blue-600/10', borderClass: 'border-blue-500/30', circleBg: 'bg-blue-500/20', circleBorder: 'border-blue-500', textClass: 'text-blue-400', description: 'Comprendre le contexte organisationnel, les ressources, les risques et les vulnérabilités.' },
                      { id: 3, name: 'PROTECT (Protéger)', bgClass: 'from-green-500/10 to-green-600/10', borderClass: 'border-green-500/30', circleBg: 'bg-green-500/20', circleBorder: 'border-green-500', textClass: 'text-green-400', description: 'Développer et mettre en œuvre des protections pour limiter ou contenir l\'impact des événements cybersécurité.' },
                      { id: 4, name: 'DETECT (Détecter)', bgClass: 'from-yellow-500/10 to-yellow-600/10', borderClass: 'border-yellow-500/30', circleBg: 'bg-yellow-500/20', circleBorder: 'border-yellow-500', textClass: 'text-yellow-400', description: 'Définir et mettre en œuvre des activités pour identifier l\'occurrence d\'événements cybersécurité.' },
                      { id: 5, name: 'RESPOND (Répondre)', bgClass: 'from-orange-500/10 to-orange-600/10', borderClass: 'border-orange-500/30', circleBg: 'bg-orange-500/20', circleBorder: 'border-orange-500', textClass: 'text-orange-400', description: 'Développer et mettre en œuvre des activités pour prendre des mesures concernant un événement cybersécurité détecté.' },
                      { id: 6, name: 'RECOVER (Récupérer)', bgClass: 'from-red-500/10 to-red-600/10', borderClass: 'border-red-500/30', circleBg: 'bg-red-500/20', circleBorder: 'border-red-500', textClass: 'text-red-400', description: 'Développer et mettre en œuvre des activités pour maintenir la résilience et restaurer les capacités affectées.' },
                    ].map((func) => (
                      <div key={func.id} className={`bg-gradient-to-br ${func.bgClass} rounded-xl p-6 border ${func.borderClass}`}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`w-12 h-12 rounded-full ${func.circleBg} border-2 ${func.circleBorder} flex items-center justify-center`}>
                            <span className={`${func.textClass} font-bold text-xl`}>{func.id}</span>
                          </div>
                          <h4 className="text-xl font-bold text-white">{func.name}</h4>
                        </div>
                        <p className="text-gray-300">{func.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* TAB: Implémentation BZ */}
              {activeTab === 'implementation' && (
                <div className="space-y-8">
                  <div className="bg-gradient-to-r from-emerald-600/20 to-green-600/20 rounded-xl p-8 border-2 border-emerald-500/50">
                    <h3 className="text-2xl font-bold text-center text-white mb-4">
                      Notre Approche Holistique
                    </h3>
                    <p className="text-gray-300 text-center text-lg">
                      Chez BZ Inc., nous implémentons le NIST 2.0 de manière <strong className="text-white">complète</strong> et <strong className="text-white">structurée</strong>. 
                      Nous ne laissons rien au hasard.
                    </p>
                  </div>

                  <div className="space-y-6">
                    <div className="bg-slate-800/50 rounded-lg p-6 border-l-4 border-indigo-500">
                      <h4 className="text-xl font-bold text-white mb-3">1. Évaluation Initiale</h4>
                      <p className="text-gray-300">
                        Nous évaluons votre posture actuelle selon les 6 fonctions du NIST 2.0. 
                        Identification des forces, des faiblesses, et des opportunités d'amélioration.
                      </p>
                    </div>

                    <div className="bg-slate-800/50 rounded-lg p-6 border-l-4 border-blue-500">
                      <h4 className="text-xl font-bold text-white mb-3">2. Planification Stratégique</h4>
                      <p className="text-gray-300">
                        Développement d'un plan d'implémentation structuré, aligné sur vos objectifs business 
                        et les exigences du cadre NIST 2.0.
                      </p>
                    </div>

                    <div className="bg-slate-800/50 rounded-lg p-6 border-l-4 border-green-500">
                      <h4 className="text-xl font-bold text-white mb-3">3. Implémentation Progressive</h4>
                      <p className="text-gray-300">
                        Mise en œuvre des contrôles et processus selon une approche priorisée, 
                        en commençant par les éléments les plus critiques.
                      </p>
                    </div>

                    <div className="bg-slate-800/50 rounded-lg p-6 border-l-4 border-yellow-500">
                      <h4 className="text-xl font-bold text-white mb-3">4. Documentation Complète</h4>
                      <p className="text-gray-300">
                        Documentation professionnelle de tous les processus, contrôles, et procédures 
                        selon les standards du NIST 2.0.
                      </p>
                    </div>

                    <div className="bg-slate-800/50 rounded-lg p-6 border-l-4 border-orange-500">
                      <h4 className="text-xl font-bold text-white mb-3">5. Suivi et Amélioration Continue</h4>
                      <p className="text-gray-300">
                        Surveillance continue de votre posture cybersécurité et amélioration continue 
                        basée sur les métriques et les évolutions du cadre.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB: Excellence BZ */}
              {activeTab === 'excellence' && (
                <div className="space-y-8">
                  <div className="bg-gradient-to-r from-purple-600/20 via-pink-600/20 to-purple-600/20 rounded-xl p-8 border-2 border-purple-500/50">
                    <h3 className="text-2xl font-bold text-center text-white mb-4">
                      Pourquoi BZ Inc. est l'Expert du Cadre NIST 2.0
                    </h3>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-xl p-6 border border-indigo-500/30">
                      <h4 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                        <Zap className="w-6 h-6 text-indigo-400" />
                        Maîtrise Approfondie
                      </h4>
                      <p className="text-gray-300">
                        Notre équipe maîtrise le cadre NIST 2.0 dans ses moindres détails. 
                        Nous comprenons non seulement les fonctions, mais aussi comment les implémenter efficacement.
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl p-6 border border-blue-500/30">
                      <h4 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                        <Target className="w-6 h-6 text-blue-400" />
                        Approche Holistique
                      </h4>
                      <p className="text-gray-300">
                        Nous ne faisons pas les choses à moitié. Nous implémentons le NIST 2.0 de manière 
                        complète, en couvrant tous les aspects de la cybersécurité.
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl p-6 border border-green-500/30">
                      <h4 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                        <TrendingUp className="w-6 h-6 text-green-400" />
                        Résultats Mesurables
                      </h4>
                      <p className="text-gray-300">
                        Nous mesurons votre progression selon le cadre NIST 2.0. Vous voyez clairement 
                        où vous en êtes et où vous allez.
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-xl p-6 border border-yellow-500/30">
                      <h4 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                        <Award className="w-6 h-6 text-yellow-400" />
                        Excellence Continue
                      </h4>
                      <p className="text-gray-300">
                        L'excellence n'est pas un objectif, c'est un processus continu. 
                        Nous vous accompagnons dans votre évolution constante.
                      </p>
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* Footer */}
            <div className="p-6 border-t border-indigo-500/30 bg-slate-900/50">
              <div className="flex items-center justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-200 font-semibold shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 hover:scale-105"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

